import { Check, Moon, Sun, SunMoon, type LucideIcon } from "lucide-react";
import { DropdownMenu } from "radix-ui";

import { cn } from "@/ui/cn";

import { useTheme, type ThemePreference } from "./theme";

/** The three choices, in order. `iconClass` evens out an icon drawn smaller than the others. */
export const THEME_OPTIONS: readonly {
  value: ThemePreference;
  label: string;
  icon: LucideIcon;
  iconClass?: string;
}[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "Match device", icon: SunMoon, iconClass: "scale-[1.18]" },
];

const itemClass =
  "flex min-h-10 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-sm font-medium outline-none select-none data-[highlighted]:bg-surface-muted [&_svg]:size-[18px] [&_svg]:shrink-0";

/**
 * The theme choices as radio items, for any dropdown menu (this switch and the account menu). Picking one
 * keeps the menu open, so the change is seen at once and another can be tried.
 */
export function ThemeMenuItems({ itemClassName = itemClass }: { itemClassName?: string }) {
  const { preference, setPreference } = useTheme();
  return (
    <DropdownMenu.RadioGroup
      value={preference}
      onValueChange={(value) => setPreference(value as ThemePreference)}
    >
      {THEME_OPTIONS.map(({ value, label, icon: Icon, iconClass }) => (
        <DropdownMenu.RadioItem
          key={value}
          value={value}
          onSelect={(event) => event.preventDefault()}
          className={itemClassName}
        >
          <Icon aria-hidden="true" className={cn("text-muted", iconClass)} />
          <span className="grow">{label}</span>
          <DropdownMenu.ItemIndicator>
            <Check aria-hidden="true" className="text-primary" />
          </DropdownMenu.ItemIndicator>
        </DropdownMenu.RadioItem>
      ))}
    </DropdownMenu.RadioGroup>
  );
}

/** One round button showing the current theme; it opens the three choices (pages without the account menu). */
export function ThemeSwitch({ className }: { className?: string }) {
  const { preference } = useTheme();
  const current = THEME_OPTIONS.find((o) => o.value === preference) ?? THEME_OPTIONS[2]!;
  const Icon = current.icon;
  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={`Theme: ${current.label}`}
          title={`Theme: ${current.label}`}
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-full border border-border bg-surface text-muted transition-colors hover:bg-surface-muted hover:text-ink data-[state=open]:bg-surface-muted data-[state=open]:text-ink",
            className,
          )}
        >
          <Icon className={cn("size-[18px]", current.iconClass)} aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          aria-label="Theme"
          className="z-50 min-w-48 rounded-xl border border-border bg-surface p-1.5 text-ink shadow-xl"
        >
          <ThemeMenuItems />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
